import moment from 'moment';
import ProjectSearch from '@/models/ProjectSearch';
import ProjectSearchRepository from '@/repositories/ProjectSearchRepository';
import { buildDeliveryOrdersQuery } from '@/modules/Logistics/helpers/DeliveryFiltersHelper';
import type { DeliveryOrderFilters } from '@/modules/Logistics/helpers/DeliveryFiltersHelper';
import type {
    DeliveryInternalOrder,
    DeliveryInternalOrderTableRow,
    DeliveryOrder,
    DeliveryOrderTableRow,
    ProjectInternalOrderDates,
} from '@/modules/Logistics/interfaces/DeliveryOrderInterfaces';

const EMPTY_DATE_VALUES = new Set(['', '1900-01-01T00:00']);

export async function fetchDeliveryOrders(filters: DeliveryOrderFilters): Promise<DeliveryOrder[]> {
    await ProjectSearch.getAllWithInternalOrders(buildDeliveryOrdersQuery(filters));

    return normalizeDeliveryOrders(ProjectSearchRepository.getAll());
}

export function getInternalOrderDates(internalOrder: DeliveryInternalOrder): ProjectInternalOrderDates | null {
    return internalOrder.projectInternalOrderDates?.[0] || null;
}

export function isUsableDeliveryDate(date: string | null | undefined): boolean {
    if (!date || EMPTY_DATE_VALUES.has(date)) {
        return false;
    }

    return moment(date).isValid() && moment(date).year() >= 1970;
}

export function getInternalOrderDeliveryDateLabel(internalOrders: DeliveryInternalOrder[]): string {
    if (internalOrders.some((internalOrder) => isUsableDeliveryDate(getInternalOrderDates(internalOrder)?.delivery))) {
        return 'Estimated delivery date';
    }

    return 'Requested delivery date';
}

export function getInternalOrderDeliveryDate(internalOrder: DeliveryInternalOrder): string {
    const dates = getInternalOrderDates(internalOrder);
    return dates?.delivery || dates?.deliveryDate || internalOrder.delivery || internalOrder.deliveryDate || '';
}

export function formatDeliveryOrderAddress(order: DeliveryOrder): string {
    const postalCodeAndCity = [order.clientPoNumber, order.clientCity].filter(Boolean).join(' ');

    return [order.clientStreetNameAndNumber, postalCodeAndCity].filter(Boolean).join(', ');
}

export function isDeliveryStatusCompleted(
    status: string | { code?: string; name?: string } | null | undefined,
): boolean {
    const statusCode = typeof status === 'string' ? status : status?.code || status?.name || '';
    return statusCode.toUpperCase() === 'FINISHED' || statusCode.toUpperCase() === 'COMPLETED';
}

export function createDeliveryOrderTableRows(
    orders: DeliveryOrder[],
    orderStatuses: Array<{ id: string; name: string; code: string }>,
    selectedOrderIds: string[] = [],
): DeliveryOrderTableRow[] {
    const selectedIds = new Set(selectedOrderIds);

    return orders.map((order) => ({
        ...order,
        address: formatDeliveryOrderAddress(order),
        isCompleted: isDeliveryStatusCompleted(getStatusById(order.orderStatusId, orderStatuses)),
        isSelected: selectedIds.has(order.id),
        projectInternalOrders: order.projectInternalOrders.map(
            (internalOrder): DeliveryInternalOrderTableRow => ({
                ...internalOrder,
                isCompleted: isDeliveryStatusCompleted(internalOrder.orderStatus),
            }),
        ),
    }));
}

function getStatusById(
    statusId: string,
    orderStatuses: Array<{ id: string; name: string; code: string }>,
): string | { code?: string; name?: string } | null {
    const status = orderStatuses.find((orderStatus) => orderStatus.id === statusId);

    return status || null;
}

export function sortDeliveryOrdersByStatus(
    firstOrder: DeliveryOrderTableRow,
    secondOrder: DeliveryOrderTableRow,
): number {
    firstOrder.projectInternalOrders.sort(
        (firstInternalOrder, secondInternalOrder) =>
            Number(secondInternalOrder.isCompleted) - Number(firstInternalOrder.isCompleted),
    );
    secondOrder.projectInternalOrders.sort(
        (firstInternalOrder, secondInternalOrder) =>
            Number(secondInternalOrder.isCompleted) - Number(firstInternalOrder.isCompleted),
    );

    const firstOrderInternalOrdersCompleted =
        firstOrder.projectInternalOrders.length > 0 &&
        firstOrder.projectInternalOrders.every((internalOrder) => internalOrder.isCompleted);
    const secondOrderInternalOrdersCompleted =
        secondOrder.projectInternalOrders.length > 0 &&
        secondOrder.projectInternalOrders.every((internalOrder) => internalOrder.isCompleted);

    if (firstOrderInternalOrdersCompleted !== secondOrderInternalOrdersCompleted) {
        return firstOrderInternalOrdersCompleted ? -1 : 1;
    }

    if (firstOrder.isCompleted !== secondOrder.isCompleted) {
        return firstOrder.isCompleted ? -1 : 1;
    }

    return 0;
}

/*
 * SAFETY LEGACY: The previous delivery API received fully constructed spreadsheet rows.
 * Keep this implementation in case the client needs to restore the old payload format.
 *
 * export function buildDeliveryCreateRows(groups: SelectedInternalOrdersByOrder[]): Array<Record<string, any>> {
 *     return groups.reduce<Array<Record<string, any>>>((rows, group) => {
 *         group.internalOrders.forEach((internalOrder) => {
 *             const dates = getInternalOrderDates(internalOrder);
 *             rows.push({
 *                 'Dokument': internalOrder.type || '',
 *                 'St.Dok.': internalOrder.orderNumber || '',
 *                 'Stranka': group.order.clientName || '',
 *                 'Kraj': group.order.clientCity || '',
 *                 'Naslov': group.order.clientStreetNameAndNumber || '',
 *                 'Status T':
 *                     typeof internalOrder.orderStatus === 'string'
 *                         ? internalOrder.orderStatus
 *                         : internalOrder.orderStatus?.name || internalOrder.orderStatus?.code || '',
 *                 'Status proizvodnje': '',
 *                 'Datum dok.': internalOrder.createdAt || group.order.createdAt || '',
 *                 'Datum V proizvodnji': dates?.inProduction || '',
 *                 'Projekt/Veza': group.order.connection || group.order.orderNumber || '',
 *                 'Sistem': internalOrder.system || '',
 *                 'KOS': dates?.numOfOrders || 0,
 *                 'Dobavnica': '',
 *                 'Izdelano': dates?.manufacturedDate || '',
 *                 'Razvoz': '',
 *                 'Rok izdobave': getInternalOrderDeliveryDate(internalOrder),
 *                 'Narocila': internalOrder.orderNumber || '',
 *             });
 *         });
 *
 *         return rows;
 *     }, []);
 * }
 */

export function normalizeDeliveryOrders(orders: ProjectSearch[]): DeliveryOrder[] {
    return orders
        .map((order) => ({
            id: String(order.id),
            orderNumber: order.orderNumber || '',
            clientName: order.clientName || '',
            clientPhone: order.clientPhone || '',
            clientCity: order.clientCity || '',
            clientPoNumber: order.clientPoNumber || '',
            clientStreetNameAndNumber: order.clientStreetNameAndNumber || '',
            deliveryRegion: order.deliveryRegion || '',
            connection: order.connection || '',
            createdAt: order.createdAt || '',
            orderCreatedAt: order.orderCreatedAt || order.createdAt || '',
            deliveryDate: order.deliveryDate || '',
            orderStatusName: order.orderStatusName || '',
            orderStatusId: order.orderStatusId || '',
            assignedUserName: order.assignedUserName || '',
            price: order.price,
            projectInternalOrders: (order.projectInternalOrders || []) as DeliveryInternalOrder[],
        }))
        .filter((order) => order.projectInternalOrders.length > 0);
}
